<script setup>

import {computed, onMounted, reactive, ref} from "vue";
import {useAdminStore} from "@/store/admin";
import {useToast} from "vue-toast-notification";
import MainPreloader from "@/components/shared/MainPreloader.vue";
import EmptyCard from "@/components/shared/EmptyCard.vue";
import FormInput from "@/components/ui/FormInput.vue";
import CheckBox from "@/components/ui/CheckBox.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import AdminModal from "@/components/AdminView/AdminModal.vue";
import TablePager from "@/components/shared/TablePager.vue";

const adminStore = useAdminStore();
const toast = useToast();

const items = computed(() => adminStore.orders);

const PER_PAGE_STORAGE_KEY = 'adminOrdersPerPage';
const currentPage = ref(0);

const PER_PAGE = ref(parseInt(localStorage.getItem(PER_PAGE_STORAGE_KEY)) || 50);

const countPages = computed(() => Math.ceil((adminStore.total || 0) / PER_PAGE.value));
const hasMoreData = computed(() => (adminStore.total || 0) > (currentPage.value + 1) * PER_PAGE.value);

function buildParams() {
    const params = {page: currentPage.value + 1, per_page: PER_PAGE.value};
    if (filterStatus.value) params.status = filterStatus.value;
    const dateFrom = parseDateInput(filterDateFrom.value);
    const dateTo = parseDateInput(filterDateTo.value);
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
}

async function loadOrders() {
    await adminStore.loadList('orders', buildParams());
    if (currentPage.value > 0 && currentPage.value >= countPages.value) {
        currentPage.value = Math.max(0, countPages.value - 1);
        await adminStore.loadList('orders', buildParams());
    }
}

async function setPage(p) {
    if (p < 0 || p >= countPages.value) return;
    currentPage.value = p;
    await loadOrders();
}

function setPerPage(n) {
    const val = parseInt(n);
    if (!val || val < 1) return;
    PER_PAGE.value = val;
    localStorage.setItem(PER_PAGE_STORAGE_KEY, String(val));
    currentPage.value = 0;
    loadOrders();
}

const pagination = {
    loading: computed(() => adminStore.loading),
    currentPage,
    countPages,
    hasMoreData,
    perPage: PER_PAGE,
    setPage,
    setPerPage,
    firstPage: () => setPage(0),
    decPage: () => setPage(currentPage.value - 1),
    incPage: () => setPage(currentPage.value + 1),
    lastPage: () => setPage(countPages.value - 1),
};

const editing = ref(null);
const showForm = ref(false);
const showItems = ref(false);
const currentOrderItems = ref([]);
const form = reactive(defaultForm());

// Фильтры
const filterStatus = ref("");
const filterDateFrom = ref("");
const filterDateTo = ref("");

function parseDateInput(value) {
    if (!value) return "";
    const parts = value.split(".");
    if (parts.length !== 3) return "";
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
}

function defaultForm() {
    return {
        status: "wait",
        fio: "",
        email: "",
        phone: "",
        use_delivery: false,
        comment: "",
    };
}

function resetForm() {
    Object.assign(form, defaultForm());
}

function formatDate(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("ru-RU") + " " + d.toLocaleTimeString("ru-RU", {hour: "2-digit", minute: "2-digit"});
}

function formatStatus(status) {
    if (status === "wait") return "Ожидает";
    if (status === "paid") return "Оплачен";
    return status || "—";
}

function statusClass(status) {
    if (status === "wait") return "adminOrders__status--wait";
    if (status === "paid") return "adminOrders__status--paid";
    return "";
}

function openEdit(item) {
    editing.value = item;
    showForm.value = true;
    Object.assign(form, {
        status: item.status || "wait",
        fio: item.fio || "",
        email: item.email || "",
        phone: item.phone || "",
        use_delivery: !!item.use_delivery,
        comment: item.comment || "",
    });
}

function openItems(order) {
    currentOrderItems.value = order.order_items || [];
    showItems.value = true;
}

function cancelForm() {
    editing.value = null;
    showForm.value = false;
    resetForm();
}

async function saveHandler() {
    const payload = {
        status: form.status,
        fio: form.fio || null,
        email: form.email || null,
        phone: form.phone || null,
        use_delivery: form.use_delivery,
        comment: form.comment || null,
    };
    const ok = await adminStore.updateItem('orders', editing.value.id, payload);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        toast.success("Заказ обновлён");
        cancelForm();
        await loadOrders();
    }
}

async function deleteHandler(item) {
    if (!confirm(`Удалить заказ #${item.order_number || item.id}?`)) return;
    const ok = await adminStore.deleteItem('orders', item.id);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        toast.success("Заказ удалён");
        await loadOrders();
    }
}

function cancelOrderItems() {
    showItems.value = false;
    currentOrderItems.value = [];
}

function calculateTotal(order) {
    if (!order.order_items || !order.order_items.length) return "—";
    const total = order.order_items.reduce((sum, item) => {
        const qnt = item.qnt || 0;
        const price = item.price || 0;
        return sum + qnt * price;
    }, 0);
    return total.toFixed(2);
}

function applyFilters() {
    currentPage.value = 0;
    loadOrders();
}

function resetFilters() {
    filterStatus.value = "";
    filterDateFrom.value = "";
    filterDateTo.value = "";
    currentPage.value = 0;
    loadOrders();
}

onMounted(() => {
    loadOrders();
});

</script>

<template>
  <div class="adminOrders">
    <MainPreloader
        v-if="adminStore.loading"
        position="fixed"
        :size="80"
        :stroke="3"
    />

    <div class="adminOrders__toolbar">
      <div class="adminOrders__toolbarTitle">Заказы</div>
    </div>

    <!-- Фильтры -->
    <div class="adminOrders__filters">
      <div class="adminOrders__filterRow">
        <div class="adminOrders__filterField">
          <label class="adminOrders__label">Статус</label>
          <select v-model="filterStatus" class="adminOrders__select">
            <option value="">Все</option>
            <option value="wait">Ожидает</option>
            <option value="paid">Оплачен</option>
          </select>
        </div>
        <div class="adminOrders__filterField">
          <label class="adminOrders__label">Дата от</label>
          <input type="text" v-model="filterDateFrom" placeholder="дд.мм.гггг" class="adminOrders__dateInput"/>
        </div>
        <div class="adminOrders__filterField">
          <label class="adminOrders__label">Дата до</label>
          <input type="text" v-model="filterDateTo" placeholder="дд.мм.гггг" class="adminOrders__dateInput"/>
        </div>
        <div class="adminOrders__filterActions">
          <ActionButton @buttonClick="applyFilters">
            <template #caption>Применить</template>
          </ActionButton>
          <ActionButton @buttonClick="resetFilters">
            <template #caption>Сбросить</template>
          </ActionButton>
        </div>
      </div>
    </div>

    <!-- Форма редактирования заказа -->
    <AdminModal :visible="showForm" title="Редактирование заказа" @close="cancelForm">
      <div class="adminOrders__formGrid">
        <div class="adminOrders__field">
          <label class="adminOrders__label">Статус</label>
          <select v-model="form.status" class="adminOrders__select">
            <option value="wait">Ожидает</option>
            <option value="paid">Оплачен</option>
          </select>
        </div>
        <div class="adminOrders__field">
          <label class="adminOrders__label">ФИО</label>
          <form-input v-model="form.fio"/>
        </div>
        <div class="adminOrders__field">
          <label class="adminOrders__label">Email</label>
          <form-input v-model="form.email"/>
        </div>
        <div class="adminOrders__field">
          <label class="adminOrders__label">Телефон</label>
          <form-input v-model="form.phone"/>
        </div>
        <div class="adminOrders__field adminOrders__field--wide">
          <label class="adminOrders__label">Комментарий</label>
          <form-input v-model="form.comment" rows="3"/>
        </div>
        <div class="adminOrders__field">
          <check-box v-model="form.use_delivery" text="Доставка"/>
        </div>
      </div>
      <template #footer>
        <ActionButton @buttonClick="cancelForm">
          <template #caption>Отмена</template>
        </ActionButton>
        <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="saveHandler">
          <template #caption>Сохранить</template>
        </ActionButton>
      </template>
    </AdminModal>

    <!-- Список позиций заказа -->
    <AdminModal :visible="showItems" title="Позиции заказа" @close="cancelOrderItems">
      <div class="adminOrders__itemsList">
        <EmptyCard v-if="!currentOrderItems.length && !adminStore.loading"/>
        <table v-else class="adminOrders__itemsTable">
          <thead>
          <tr>
            <th>Товар</th>
            <th>Кол-во</th>
            <th>Цена</th>
            <th>Сумма</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in currentOrderItems" :key="item.id">
            <td>{{ item.name || '—' }}</td>
            <td>{{ item.qnt ?? '—' }}</td>
            <td>{{ item.price ? item.price.toFixed(2) : '—' }}</td>
            <td>{{ item.qnt && item.price ? (item.qnt * item.price).toFixed(2) : '—' }}</td>
          </tr>
          </tbody>
        </table>
      </div>
    </AdminModal>

    <EmptyCard v-if="!items.length && !adminStore.loading"/>

    <TablePager
        v-if="items.length && pagination.countPages.value > 1"
        :pagination="pagination"
    />

    <table v-if="items.length" class="adminTable">
      <thead>
      <tr>
        <th>№</th>
        <th>Дата</th>
        <th>Статус</th>
        <th>ФИО</th>
        <th>Email</th>
        <th>Телефон</th>
        <th>Доставка</th>
        <th>Сумма</th>
        <th>Действия</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="item in items" :key="item.id">
        <td>{{ item.order_number || '—' }}</td>
        <td>{{ formatDate(item.created_at) }}</td>
        <td>
          <span :class="['adminOrders__status', statusClass(item.status)]">
            {{ formatStatus(item.status) }}
          </span>
        </td>
        <td>{{ item.fio || '—' }}</td>
        <td>{{ item.email || '—' }}</td>
        <td>{{ item.phone || '—' }}</td>
        <td>{{ item.use_delivery ? 'Да' : 'Нет' }}</td>
        <td>{{ calculateTotal(item) }}</td>
        <td class="adminOrders__actions">
          <button class="adminOrders__actionButton" title="Позиции" @click="openItems(item)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="adminOrders__actionButton adminOrders__actionButton--edit" title="Редактировать" @click="openEdit(item)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button class="adminOrders__actionButton adminOrders__actionButton--danger" title="Удалить" @click="deleteHandler(item)">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6h18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </td>
      </tr>
      </tbody>
    </table>

    <TablePager
        v-if="items.length && pagination.countPages.value > 1"
        :pagination="pagination"
    />
  </div>
</template>

<style scoped lang="scss">
.adminOrders {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__toolbarTitle {
    color: var(--cd-base-green);
    font-size: 16px;
    font-family: var(--cd-default-font);
  }

  &__filters {
    background: var(--cd-card-bg);
    border-radius: 20px;
    padding: 20px;
  }

  &__filterRow {
    display: flex;
    gap: 16px;
    align-items: flex-end;
    flex-wrap: wrap;

    @include respond-to('md') {
      flex-direction: column;
      align-items: stretch;
    }
  }

  &__filterField {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 180px;
  }

  &__filterActions {
    display: flex;
    gap: 8px;
    align-items: flex-end;
  }

  &__formGrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @include respond-to('md') {
      grid-template-columns: 1fr;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &--wide {
      grid-column: 1 / -1;
    }
  }

  &__label {
    color: var(--cd-gray-alt);
    font-size: 13px;
  }

  &__select {
    width: 100%;
    height: 48px;
    border-radius: 20px;
    padding: 5px 20px;
    background: var(--cd-field-bg);
    color: var(--cd-field-color);
    border: 1px solid var(--cd-field-border);
    font-size: 14px;
    font-family: var(--cd-default-font);

    &:focus {
      outline: none;
      border-color: var(--cd-base-green);
      box-shadow: 0 0 5px rgba(98, 192, 100, 0.3);
    }
  }

  &__dateInput {
    width: 100%;
    height: 48px;
    border-radius: 20px;
    padding: 5px 16px;
    background: var(--cd-field-bg);
    color: var(--cd-field-color);
    border: 1px solid var(--cd-field-border);
    font-size: 14px;
    font-family: var(--cd-default-font);

    &:focus {
      outline: none;
      border-color: var(--cd-base-green);
      box-shadow: 0 0 5px rgba(98, 192, 100, 0.3);
    }
  }

  &__status {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 13px;

    &--wait {
      background: #FFF3CD;
      color: #856404;
    }

    &--paid {
      background: #D4EDDA;
      color: #155724;
    }
  }

  &__itemsList {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__itemsTable {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 4px;
    text-align: left;

    th {
      color: var(--cd-text-muted);
      font-size: 12px;
      font-weight: 400;
      padding: 0 12px 4px;
    }

    td {
      background: var(--cd-card-bg);
      padding: 8px 12px;
      font-size: 13px;
      color: var(--cd-text);

      &:first-child {
        border-radius: 8px 0 0 8px;
      }

      &:last-child {
        border-radius: 0 8px 8px 0;
      }
    }
  }

  &__actions {
    vertical-align: middle;
    white-space: nowrap;
    font-size: 0;

    .adminOrders__actionButton + .adminOrders__actionButton {
      margin-left: 6px;
    }
  }

  &__actionButton {
    background: transparent;
    border: 1px solid var(--cd-base-green);
    color: var(--cd-base-green);
    border-radius: 8px;
    padding: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    &:hover {
      background: var(--cd-base-green);
      color: #ffffff;
    }

    &--edit {
      border-color: #007BFF;
      color: #007BFF;

      &:hover {
        background: #007BFF;
        color: #ffffff;
      }
    }

    &--danger {
      border-color: var(--cd-danger);
      color: var(--cd-danger);

      &:hover {
        background: var(--cd-danger);
        color: #ffffff;
      }
    }
  }
}

.adminTable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
  text-align: left;

  th {
    color: var(--cd-text-muted);
    font-size: 12px;
    font-weight: 400;
    padding: 0 16px 4px;
  }

  td {
    background: var(--cd-card-bg);
    padding: 12px 16px;
    font-size: 14px;
    color: var(--cd-text);

    &:first-child {
      border-radius: 12px 0 0 12px;
    }

    &:last-child {
      border-radius: 0 12px 12px 0;
    }
  }
}
</style>
